import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig, isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { pushDataLayerEvent, currentCurrency } from '@theme/analytics';

/** upsell_id / upsell_type for each panel, per the event tracking plan. */
const UPSELL_META = {
  frame: { upsell_id: 'frame_upgrade', upsell_type: 'product_option' },
  set: { upsell_id: 'complete_the_set', upsell_type: 'cross_sell' },
};

// Delay before opening so the auto-opening cart drawer enters the top layer
// first and this modal stacks above it.
const OPEN_DELAY = 250;

// Longest we hold the checkout hand-off waiting for gtag to confirm an
// upsell event was sent (see #sendBeforeNavigation).
const ANALYTICS_FLUSH_TIMEOUT = 500;

// Debounce for cart re-fetches when cart:update events fire in bursts.
const SYNC_DEBOUNCE = 150;

// Dispatch this on `document` to open the modal from elsewhere (e.g. the
// frame upsell hero block on the cart page).
export const OPEN_EVENT = 'add-to-cart-upsell:open';

/**
 * Add-to-cart upsell modal.
 *
 * Product context: keeps a snapshot of the cart and, whenever the theme's
 * cart:update event fires, diffs quantities for this product's variants.
 * When config.enableFrame is set, a genuine increase of an unframed variant
 * opens the "Get it framed" panel. Otherwise (or when there's nothing to
 * frame-upsell), any genuine quantity increase of this product opens
 * "Complete the set" — when config.enableSet is set and the product belongs
 * to an inspirational collection with available siblings.
 *
 * Cart context: never auto-opens. Listens for the OPEN_EVENT document event
 * and opens the frame panel, listing every unframed line in the cart, each
 * with its own frame style tiles.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLScriptElement} data - JSON configuration payload.
 * @property {HTMLElement} [frameItems] - Container of frame item rows.
 * @property {HTMLTemplateElement} [frameRowTemplate] - Row template (cart context).
 * @property {HTMLTemplateElement} [frameTileTemplate] - Tile template (cart context).
 * @property {HTMLButtonElement} [frameCta] - Frame panel CTA.
 * @property {HTMLElement} [frameCtaLabel] - Frame panel CTA label.
 * @property {HTMLElement} [frameError] - Error element (frame panel).
 * @property {HTMLElement} [setProducts] - Container of set product tiles.
 * @property {HTMLElement} [setCount] - "N prints selected" element.
 * @property {HTMLElement} [setTotal] - Set total price element.
 * @property {HTMLButtonElement} [setCta] - Set panel CTA.
 * @property {HTMLElement} [setCtaLabel] - Set panel CTA label.
 * @property {HTMLElement} [setError] - Error element (set panel).
 *
 * @extends {Component<Refs>}
 */
class AddToCartUpsell extends Component {
  requiredRefs = ['dialog', 'data'];

  #abortController = new AbortController();

  /** @type {any} */
  #config = null;

  /** @type {Map<string, number> | null} Quantity per variant id of this product */
  #baseline = null;

  /** @type {number | undefined} */
  #syncTimer;

  /** Set by a "Buy as a set" cart update: the next sync re-baselines without opening a panel. */
  #skipNextOpen = false;

  /** @type {'frame' | 'set' | null} */
  #currentPanel = null;

  /**
   * What triggered the panel currently open (or last opened), reported as
   * `placement` on upsell_shown/accepted/declined. Product-context panels
   * are always the post-add-to-cart modal; cart-context panels set this
   * per invocation — see #openCartFramePanel callers.
   * @type {string}
   */
  #panelPlacement = 'post_add_to_cart_modal';

  #busy = false;

  /**
   * Set while a cart-context frame panel is being built (cart + product
   * fetches), when dialog.open is still false. Guards against a second click
   * on the hero / row link building the panel (and reporting upsell_shown)
   * twice.
   */
  #building = false;

  /** Set between #open() reporting upsell_shown and the dialog actually showing. */
  #showPending = false;

  /** Whether the frame panel was opened by intercepting a checkout attempt. */
  #checkoutIntent = false;

  /** Set once we hand over to checkout, so we never intercept again. */
  #proceeding = false;

  /** @type {Map<string, any>} Cache of /products/{handle}.js payloads */
  #productCache = new Map();

  /** @type {number | undefined} */
  #linksTimer;

  /** @type {IntersectionObserver | null} Watches injected "Add a frame?" row links. */
  #rowLinkObserver = null;

  /** @type {Set<string>} Cart line keys whose row link was already reported as viewed. */
  #reportedRowLinks = new Set();

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#config = JSON.parse(this.refs.data.textContent || 'null');
    } catch (error) {
      console.error('[add-to-cart-upsell] Invalid configuration JSON', error);
      return;
    }

    if (!this.#config) return;

    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartActivity, { signal });
    document.addEventListener(OPEN_EVENT, this.#onOpenRequest, { signal });

    // Keep the closing animation when Esc is pressed.
    this.refs.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.dismiss();
      },
      { signal }
    );

    // Close when clicking the backdrop.
    this.refs.dialog.addEventListener(
      'click',
      (event) => {
        if (isClickedOutside(event, this.refs.dialog)) this.dismiss();
      },
      { signal }
    );

    // Establish the cart baseline for diffing.
    if (this.#config.context === 'product') {
      this.#syncCart();
    }

    if (this.#config.context === 'cart') {
      // Intercept checkout attempts while unframed prints are in the cart.
      document.addEventListener('click', this.#onCheckoutClick, { capture: true, signal });
      document.addEventListener('submit', this.#onCheckoutSubmit, { capture: true, signal });

      // "Add a frame" links under unframed lines on the cart page.
      this.#scheduleCartRowLinks();
      document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleCartRowLinks, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    if (this.#linksTimer) clearTimeout(this.#linksTimer);
    this.#rowLinkObserver?.disconnect();
  }

  /* ------------------------------------------------------------------ */
  /* Triggering                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Debounced reaction to any cart:update event.
   */
  #onCartActivity = (/** @type {Event} */ event) => {
    if (this.#config.context !== 'product') return;
    // A "Buy as a set" add (assets/buy-set.js) already includes the set, so
    // re-baseline the cart without offering an upsell for it.
    if (/** @type {any} */ (event)?.detail?.data?.source === 'buy-set-block') this.#skipNextOpen = true;
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(() => this.#syncCart(), SYNC_DEBOUNCE);
  };

  /**
   * Fetches the cart, diffs this product's variant quantities against the
   * previous snapshot and triggers the appropriate panel.
   */
  async #syncCart() {
    const skipOpen = this.#skipNextOpen;
    this.#skipNextOpen = false;

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch (error) {
      console.error('[add-to-cart-upsell] Unable to fetch cart', error);
      return;
    }

    const previous = this.#baseline;
    this.#baseline = this.#quantitiesFor(cart);

    // First fetch only establishes the baseline.
    if (!previous || skipOpen) return;
    if (this.refs.dialog.open || this.#busy || this.#building || this.#showPending) return;

    let noFrameIncrease = null;
    let anyIncrease = null;

    for (const variant of this.#config.variants) {
      const id = String(variant.id);
      const delta = (this.#baseline.get(id) ?? 0) - (previous.get(id) ?? 0);
      if (delta <= 0) continue;

      if (!anyIncrease || delta > anyIncrease.delta) anyIncrease = { variant, delta };

      if (this.#config.enableFrame && this.#config.frameOptionIndex >= 0) {
        const frameValue = variant.options[this.#config.frameOptionIndex];
        if (this.#isNoFrame(frameValue, this.#config.noFrameValue)) {
          noFrameIncrease = { variant, delta };
        }
      }
    }

    // The frame panel takes priority when it has something to offer and
    // actually opens; otherwise (disabled, already dismissed this session,
    // or no framed counterpart to offer) any genuine add-to-cart of this
    // product falls back to "Complete the set" (when enabled and siblings
    // exist) — no longer gated on the added variant being framed.
    let frameOpened = false;
    if (this.#config.enableFrame && noFrameIncrease) {
      frameOpened = this.#prepareProductFramePanel(noFrameIncrease.variant, noFrameIncrease.delta);
    }

    if (!frameOpened && this.#config.enableSet && anyIncrease && this.#config.set?.products?.length) {
      const frameValue =
        this.#config.frameOptionIndex >= 0 ? anyIncrease.variant.options[this.#config.frameOptionIndex] : undefined;
      this.#prepareSetPanel(frameValue, cart);
    }
  }

  /**
   * Opens the frame panel on request (hero button / cart row links).
   */
  #onOpenRequest = () => {
    if (!this.#isPrimaryCartInstance()) return;
    this.#openCartFramePanel({ checkoutIntent: false, placement: 'cart_hero' });
  };

  /**
   * Intercepts clicks on checkout buttons while unframed prints are in the
   * cart, showing the frame upsell first. "Continue with no frame" (or a
   * successful upgrade) proceeds to checkout.
   *
   * @param {MouseEvent} event
   */
  #onCheckoutClick = (event) => {
    if (!this.#isPrimaryCartInstance() || this.#proceeding) return;
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[name="checkout"], .cart__checkout-button');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#openCartFramePanel({ checkoutIntent: true, placement: 'cart_checkout' });
  };

  /**
   * @param {SubmitEvent} event
   */
  #onCheckoutSubmit = (event) => {
    if (!this.#isPrimaryCartInstance() || this.#proceeding) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/checkout')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#openCartFramePanel({ checkoutIntent: true, placement: 'cart_checkout' });
  };

  /**
   * If the block (and therefore this modal) is rendered more than once, only
   * the first cart-context instance responds to document-level triggers.
   *
   * @returns {boolean}
   */
  #isPrimaryCartInstance() {
    if (this.#config.context !== 'cart') return false;
    return document.querySelector('add-to-cart-upsell[data-context="cart"]') === this;
  }

  /**
   * Builds the frame panel from the live cart — one row per unframed line —
   * and opens it. With checkoutIntent, an empty result proceeds straight to
   * checkout instead.
   *
   * @param {{ checkoutIntent: boolean, placement: string }} options
   */
  async #openCartFramePanel({ checkoutIntent, placement }) {
    if (this.#busy || this.#building || this.#showPending || this.refs.dialog.open) return;

    this.#building = true;
    try {
      await this.#buildCartFramePanel({ checkoutIntent, placement });
    } finally {
      this.#building = false;
    }
  }

  /**
   * @param {{ checkoutIntent: boolean, placement: string }} options
   */
  async #buildCartFramePanel({ checkoutIntent, placement }) {
    this.#panelPlacement = placement;

    const container = this.refs.frameItems;
    const rowTemplate = this.refs.frameRowTemplate;
    const tileTemplate = this.refs.frameTileTemplate;
    if (!container || !(rowTemplate instanceof HTMLTemplateElement) || !(tileTemplate instanceof HTMLTemplateElement)) {
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch (error) {
      console.error('[add-to-cart-upsell] Unable to fetch cart', error);
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    const unframedLines = cart.items.filter((item) => this.#lineIsUnframed(item));

    if (unframedLines.length === 0) {
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    container.innerHTML = '';

    for (const line of unframedLines) {
      try {
        const productData = await this.#productByHandle(line.handle);
        const rowConfig = this.#rowConfigFromProduct(productData, line);
        if (!rowConfig) continue;

        const unframedVariant = rowConfig.variants.find(
          /** @param {any} v */ (v) => String(v.id) === String(line.variant_id)
        );
        if (!unframedVariant) continue;

        const row = this.#buildFrameRow(rowTemplate, tileTemplate, rowConfig, line);
        if (!this.#populateFrameRow(row, rowConfig, unframedVariant, line.quantity, line.image)) continue;

        row.dataset.lineKey = line.key;
        container.appendChild(row);
      } catch (error) {
        console.error('[add-to-cart-upsell] Skipping cart line', line.handle, error);
      }
    }

    if (container.children.length === 0) {
      if (checkoutIntent) this.#proceedToCheckout();
      return;
    }

    this.#checkoutIntent = checkoutIntent;
    this.#updateFrameCta();
    this.#showError(this.refs.frameError, null);
    this.#open('frame');
  }

  /**
   * @param {any} item - A cart line from the AJAX API.
   * @returns {boolean}
   */
  #lineIsUnframed(item) {
    const optionMatch = (item.options_with_values || []).some(
      /** @param {any} option */
      (option) => {
        const value = String(option.value || '').toLowerCase();
        return value.includes('no frame') || value === 'unframed';
      }
    );
    const titleMatch = String(item.variant_title || '').toLowerCase() === 'no frame';
    return optionMatch || titleMatch;
  }

  /**
   * Fetches and caches /products/{handle}.js.
   *
   * @param {string} handle
   * @returns {Promise<any>}
   */
  async #productByHandle(handle) {
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);
    const response = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Product fetch failed: ${handle}`);
    const data = await response.json();
    this.#productCache.set(handle, data);
    return data;
  }

  /**
   * Builds a row config ({ variants, frameOptionIndex, noFrameValue, frameValues })
   * from a /products/{handle}.js payload.
   *
   * @param {any} productData
   * @param {any} line - The cart line, used to identify the unframed value.
   * @returns {any | null}
   */
  #rowConfigFromProduct(productData, line) {
    const frameOptionIndex = (productData.options || []).findIndex(
      /** @param {any} option */
      (option) => String(option.name || option).toLowerCase().includes('frame')
    );
    if (frameOptionIndex < 0) return null;

    const option = productData.options[frameOptionIndex];
    const values = option.values || [];

    let noFrameValue =
      values.find(/** @param {string} v */ (v) => {
        const value = v.toLowerCase();
        return value.includes('no frame') || value === 'unframed';
      }) ?? '';

    if (!noFrameValue) {
      const lineOption = (line.options_with_values || []).find(
        /** @param {any} o */ (o) => {
          const value = String(o.value || '').toLowerCase();
          return value.includes('no frame') || value === 'unframed';
        }
      );
      noFrameValue = lineOption ? lineOption.value : '';
    }

    return {
      frameOptionIndex,
      noFrameValue,
      frameValues: values.filter(/** @param {string} v */ (v) => v !== noFrameValue),
      variants: (productData.variants || []).map(
        /** @param {any} v */
        (v) => ({
          id: v.id,
          available: v.available,
          priceCents: v.price,
          price: this.#formatMoney(v.price) ?? '',
          options: v.options,
          image: v.featured_image?.src ? this.#sizedImage(v.featured_image.src, 300) : null,
        })
      ),
    };
  }

  /**
   * Clones the row + tile templates into a populated (but not yet priced)
   * frame row for a cart line.
   *
   * @param {HTMLTemplateElement} rowTemplate
   * @param {HTMLTemplateElement} tileTemplate
   * @param {any} rowConfig
   * @param {any} line
   * @returns {HTMLElement}
   */
  #buildFrameRow(rowTemplate, tileTemplate, rowConfig, line) {
    const fragment = /** @type {DocumentFragment} */ (rowTemplate.content.cloneNode(true));
    const row = /** @type {HTMLElement} */ (fragment.querySelector('[data-frame-item]'));
    const grid = row.querySelector('.atc-upsell__frame-grid');

    row.dataset.productId = String(line.product_id);

    const title = row.querySelector('.atc-upsell__item-title');
    if (title) title.textContent = line.product_title;

    const meta = row.querySelector('.atc-upsell__item-meta');
    if (meta instanceof HTMLElement) {
      meta.hidden = line.quantity <= 1;
      meta.textContent = line.quantity > 1 ? `Qty ${line.quantity}` : '';
    }

    if (grid) {
      grid.setAttribute('aria-label', `Frame style for ${line.product_title}`);

      // The "keep unframed" link is a radio in this same group (styled as a
      // discrete text link, not a tile) so the group always has exactly one
      // checked option. It's server-rendered as the grid's only child —
      // move it to the end, after the framed tiles below.
      const keepUnframedOption = grid.querySelector('[data-frame-value=""]');

      const makeTile = (/** @type {string} */ frameValue, /** @type {string} */ label) => {
        const tileFragment = /** @type {DocumentFragment} */ (tileTemplate.content.cloneNode(true));
        const tile = /** @type {HTMLElement} */ (tileFragment.querySelector('[role="radio"]'));
        tile.dataset.frameValue = frameValue;
        const tileLabel = tile.querySelector('.atc-upsell__tile-label');
        if (tileLabel) tileLabel.textContent = label;
        return tile;
      };

      for (const value of rowConfig.frameValues) {
        grid.appendChild(makeTile(value, value));
      }

      if (keepUnframedOption) grid.appendChild(keepUnframedOption);
    }

    return row;
  }

  /**
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  #sizedImage(src, width) {
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  /**
   * Proceeds to checkout, permanently disarming interception for this page.
   */
  #proceedToCheckout() {
    this.#proceeding = true;
    window.location.href = '/checkout';
  }

  /**
   * Injects "Add a frame" links under unframed lines on the cart page.
   */
  #scheduleCartRowLinks = () => {
    if (!this.#isPrimaryCartInstance()) return;
    if (this.#linksTimer) clearTimeout(this.#linksTimer);
    this.#linksTimer = setTimeout(() => this.#injectCartRowLinks(), 200);
  };

  async #injectCartRowLinks() {
    const rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    if (rows.length === 0) return;

    let cart;
    try {
      cart = await this.#fetchCart();
    } catch (_) {
      return;
    }

    document.querySelectorAll('.atc-upsell-row-link').forEach((el) => {
      this.#rowLinkObserver?.unobserve(el);
      el.remove();
    });

    for (const item of cart.items) {
      if (!this.#lineIsUnframed(item)) continue;

      const row = document.querySelector(`.cart-items__table-row[data-key="${item.key}"]`);
      const details = row?.querySelector('.cart-items__details');
      if (!details || details.querySelector('.atc-upsell-row-link')) continue;

      const link = document.createElement('a');
      link.href = '#';
      link.className = 'atc-upsell-row-link';
      link.textContent = 'Add a frame?';
      link.style.cssText =
        'display:block;margin-top:8px;text-decoration:underline;text-underline-offset:0.25em;cursor:pointer;font-family:var(--cart-primary-font-family);font-size:0.875rem;color:var(--color-foreground,#000);';
      link.dataset.lineKey = item.key;
      link.dataset.productId = String(item.product_id);
      link.dataset.productName = item.product_title;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        // The row link isn't inside a [data-analytics-component-source]
        // element, so analytics.js's delegated click tracking skips it.
        pushDataLayerEvent('component_clicked', {
          component_source: 'cart_frame_upsell_row_link',
          label: link.textContent?.trim(),
          item_id: link.dataset.productId,
          product_name: link.dataset.productName,
        });
        this.#openCartFramePanel({ checkoutIntent: false, placement: 'cart_row_link' });
      });

      details.appendChild(link);
      this.#observeRowLink(link);
    }
  }

  /**
   * Reports component_viewed once per cart line the first time its "Add a
   * frame?" link scrolls into view. These links are injected (and re-injected
   * on every cart:update) after assets/analytics.js has scanned the page for
   * [data-analytics-component-source], so they need their own observer.
   *
   * @param {HTMLElement} link
   */
  #observeRowLink(link) {
    const lineKey = link.dataset.lineKey;
    if (!lineKey || this.#reportedRowLinks.has(lineKey)) return;

    this.#rowLinkObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#rowLinkObserver?.unobserve(entry.target);

          const { lineKey: key, productId, productName } = /** @type {HTMLElement} */ (entry.target).dataset;
          if (!key || this.#reportedRowLinks.has(key)) continue;
          this.#reportedRowLinks.add(key);

          pushDataLayerEvent('component_viewed', {
            component_source: 'cart_frame_upsell_row_link',
            label: entry.target.textContent?.trim(),
            item_id: productId,
            product_name: productName,
          });
        }
      },
      { threshold: 0.1 }
    );

    this.#rowLinkObserver.observe(link);
  }

  /* ------------------------------------------------------------------ */
  /* Frame panel                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Product context: populates the single item row for the variant that was
   * just added and opens the panel.
   *
   * @param {any} addedVariant - The unframed variant that was added.
   * @param {number} quantity - The quantity that was added.
   * @returns {boolean} Whether the frame panel actually opened — callers use
   *   this to decide whether to fall back to the set panel.
   */
  #prepareProductFramePanel(addedVariant, quantity) {
    if (!this.#config.enableFrame) return false;
    if (this.#isDismissed('frame')) return false;

    this.#panelPlacement = 'post_add_to_cart_modal';

    const container = this.refs.frameItems;
    const row = container?.querySelector('[data-frame-item]');
    if (!(row instanceof HTMLElement)) return false;

    const rowConfig = {
      variants: this.#config.variants,
      frameOptionIndex: this.#config.frameOptionIndex,
      noFrameValue: this.#config.noFrameValue,
    };

    if (!this.#populateFrameRow(row, rowConfig, addedVariant, quantity)) return false;

    const meta = row.querySelector('[data-item-meta]');
    if (meta instanceof HTMLElement) {
      meta.hidden = quantity <= 1;
      meta.textContent = quantity > 1 ? `Qty ${quantity}` : '';
    }

    row.hidden = false;
    this.#updateFrameCta();
    this.#showError(this.refs.frameError, null);
    this.#open('frame');
    return true;
  }

  /**
   * Populates a frame item row: resolves the framed counterpart for each
   * chip, prices the upgrade, hides chips with no available match and
   * selects the first framed option.
   *
   * @param {HTMLElement} row
   * @param {any} rowConfig - { variants, frameOptionIndex, noFrameValue }
   * @param {any} unframedVariant
   * @param {number} quantity
   * @param {string | null} [lineImage] - The cart line's image, if any.
   * @returns {boolean} Whether the row has at least one framed option.
   */
  #populateFrameRow(row, rowConfig, unframedVariant, quantity, lineImage = null) {
    row.dataset.unframedVariantId = String(unframedVariant.id);
    row.dataset.unframedPriceCents = String(unframedVariant.priceCents);
    row.dataset.quantity = String(quantity);

    // The print's own imagery, used as a fallback for framed tiles whose
    // variants have no image of their own.
    const fallbackImage = unframedVariant.image || lineImage || this.#config.productImage || null;

    let firstFramedTile = null;

    for (const tile of row.querySelectorAll('[data-frame-value]')) {
      if (!(tile instanceof HTMLElement)) continue;

      const frameValue = tile.dataset.frameValue ?? '';
      tile.setAttribute('aria-checked', 'false');

      // "Keep unframed" radio: always available, doesn't map to a variant.
      if (frameValue === '') {
        tile.hidden = false;
        continue;
      }

      const image = tile.querySelector('[data-tile-image]');

      const target = this.#findMatchingVariant(rowConfig, unframedVariant.options, frameValue);

      if (!target) {
        tile.hidden = true;
        continue;
      }

      tile.hidden = false;
      tile.dataset.variantId = String(target.id);
      tile.dataset.priceCents = String(target.priceCents);

      if (image instanceof HTMLImageElement) {
        const src = target.image || fallbackImage;
        image.hidden = !src;
        if (src) image.src = src;
      }

      const priceElement = tile.querySelector('[data-price]');
      if (priceElement) {
        const delta = target.priceCents - unframedVariant.priceCents;
        const formattedDelta = delta > 0 ? this.#formatMoney(delta) : null;
        priceElement.textContent = formattedDelta ? `+${formattedDelta}` : '';
      }

      if (!firstFramedTile) firstFramedTile = tile;
    }

    if (!firstFramedTile) return false;

    firstFramedTile.setAttribute('aria-checked', 'true');
    return true;
  }

  /**
   * Handles a click on a frame style tile (radio within its row).
   *
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  selectFrameOption(event) {
    const tile = event.target.closest('[data-frame-value]');
    const row = event.target.closest('[data-frame-item]');
    if (!(tile instanceof HTMLElement) || !(row instanceof HTMLElement) || tile.hidden) return;

    for (const other of row.querySelectorAll('[data-frame-value]')) {
      other.setAttribute('aria-checked', other === tile ? 'true' : 'false');
    }

    this.#updateFrameCta();
  }

  /**
   * @returns {{ row: HTMLElement, productId: string | undefined, framedVariantId: string, framedPriceCents: number, frameValue: string | undefined, unframedVariantId: string, unframedPriceCents: number, quantity: number, lineKey: string | undefined }[]}
   */
  #frameSelections() {
    const container = this.refs.frameItems;
    if (!container) return [];

    const selections = [];

    for (const row of container.querySelectorAll('[data-frame-item]')) {
      if (!(row instanceof HTMLElement) || row.hidden) continue;

      const tile = row.querySelector('[data-frame-value][aria-checked="true"]');
      if (!(tile instanceof HTMLElement)) continue;
      if ((tile.dataset.frameValue ?? '') === '') continue; // Keep unframed
      if (!tile.dataset.variantId) continue;

      selections.push({
        row,
        productId: row.dataset.productId,
        framedVariantId: tile.dataset.variantId,
        framedPriceCents: parseInt(tile.dataset.priceCents ?? '0', 10) || 0,
        frameValue: tile.dataset.frameValue,
        unframedVariantId: row.dataset.unframedVariantId ?? '',
        unframedPriceCents: parseInt(row.dataset.unframedPriceCents ?? '0', 10) || 0,
        quantity: parseInt(row.dataset.quantity ?? '1', 10) || 1,
        lineKey: row.dataset.lineKey,
      });
    }

    return selections;
  }

  #updateFrameCta() {
    const { frameCta, frameCtaLabel } = this.refs;
    if (!frameCta || !frameCtaLabel) return;

    const selections = this.#frameSelections();
    const count = selections.length;

    if (count === 0) {
      // Every row was kept unframed. Mid checkout-interception, let the CTA
      // itself carry the customer through instead of leaving them stuck.
      if (this.#checkoutIntent) {
        frameCta.disabled = false;
        frameCtaLabel.textContent = this.#config.checkoutDismissLabel || 'Continue with no frame';
      } else {
        frameCta.disabled = true;
        frameCtaLabel.textContent = 'No frames selected';
      }
      return;
    }

    frameCta.disabled = false;

    const base = count === 1 ? frameCta.dataset.labelBase || 'Upgrade my print' : `Upgrade ${count} prints`;
    const totalDelta = selections.reduce(
      (sum, s) => sum + Math.max(s.framedPriceCents - s.unframedPriceCents, 0) * s.quantity,
      0
    );
    const formattedDelta = totalDelta > 0 ? this.#formatMoney(totalDelta) : null;

    frameCtaLabel.textContent = formattedDelta ? `${base} \u00b7 +${formattedDelta}` : base;
  }

  /**
   * Confirms the frame upgrades: adds the framed variants and, in 'swap'
   * mode, decrements each corresponding unframed line.
   */
  async confirmFrame() {
    if (this.#busy) return;

    const selections = this.#frameSelections();
    if (selections.length === 0) {
      // Every row was kept unframed — nothing to add, just carry the
      // customer through to checkout if that's what brought them here.
      // "Continue with no frame" is the checkout intercept's decline.
      if (this.#checkoutIntent) {
        this.#setBusy(true);
        await this.#sendBeforeNavigation([['upsell_declined', this.#declinedParams('frame')]]);
        this.#proceedToCheckout();
      }
      return;
    }

    this.#setBusy(true);
    this.#showError(this.refs.frameError, null);

    let cart;

    try {
      // 1. Add all framed variants first so the prints never disappear from
      //    the cart mid-operation.
      const items = selections.map((s) => ({ id: Number(s.framedVariantId), quantity: s.quantity }));
      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const addResult = await addResponse.json();
      if (addResult.status) {
        throw new Error(addResult.description || addResult.message || 'Unable to add to cart');
      }

      cart = await this.#fetchCart();

      // 2. In swap mode, decrement each unframed line by the quantity upgraded.
      if (this.#config.behavior === 'swap') {
        for (const selection of selections) {
          const line =
            (selection.lineKey &&
              cart.items.find(/** @param {any} item */ (item) => item.key === selection.lineKey)) ||
            cart.items.find(
              /** @param {any} item */
              (item) => String(item.variant_id) === String(selection.unframedVariantId)
            );

          if (!line) continue;

          const newQuantity = Math.max(line.quantity - selection.quantity, 0);
          const changeResponse = await fetch(
            `${Theme.routes.cart_change_url}.js`,
            fetchConfig('json', { body: JSON.stringify({ id: line.key, quantity: newQuantity }) })
          );
          try {
            const changed = await changeResponse.json();
            if (changed && Array.isArray(changed.items)) cart = changed;
          } catch (_) {
            // Non-JSON response — re-fetch the cart below.
            cart = await this.#fetchCart();
          }
        }
      }

      this.#baseline = this.#quantitiesFor(cart);
    } catch (error) {
      // Only a genuine cart mutation failure reaches here.
      console.error('[add-to-cart-upsell] Frame upgrade failed', error);
      this.#showError(
        this.refs.frameError,
        'Something went wrong updating your basket. Please try again.'
      );
      this.#setBusy(false);
      return;
    }

    this.#setBusy(false);

    // The basket is updated — everything below is best-effort UI work and
    // must never surface an error to the customer.

    // One row per unframed cart line was upgraded — report each separately
    // so multi-line confirms aren't collapsed into a single event.
    /** @type {[string, Record<string, unknown>][]} */
    const acceptedEvents = selections.map((selection) => {
      const priceBefore = selection.unframedPriceCents / 100;
      const priceAfter = selection.framedPriceCents / 100;
      return [
        'upsell_accepted',
        {
          ...UPSELL_META.frame,
          placement: this.#panelPlacement,
          item_id: selection.productId || this.#config?.productId,
          product_name: this.#productName(selection.row),
          frame_selected: selection.frameValue,
          price_before: priceBefore,
          price_after: priceAfter,
          upsell_value: (priceAfter - priceBefore) * selection.quantity,
          // £ value of the accepted upsell — the framed variant that was
          // actually added, same price × quantity convention as
          // add_to_cart_detail's `value` (not the upgrade delta above).
          value: priceAfter * selection.quantity,
          currency: currentCurrency(),
        },
      ];
    });

    // Opened by a checkout attempt: report the accepts (before the page
    // unloads), then hand straight over to checkout.
    if (this.#checkoutIntent) {
      this.#setBusy(true);
      await this.#sendBeforeNavigation(acceptedEvents);
      this.#proceedToCheckout();
      return;
    }

    for (const [name, params] of acceptedEvents) pushDataLayerEvent(name, params);

    this.#markDismissed('frame');

    this.#notifyCartUpdated(cart, selections[0].framedVariantId);
    await this.close();

    // Chain into "Complete the set" now that a framed print is in the cart.
    try {
      if (this.#config.context === 'product' && this.#config.enableSet && this.#config.set?.products?.length) {
        const framedVariant = this.#config.variants.find(
          /** @param {any} v */ (v) => String(v.id) === selections[0].framedVariantId
        );
        if (framedVariant) {
          const frameValue = framedVariant.options[this.#config.frameOptionIndex];
          this.#prepareSetPanel(frameValue, cart);
        }
      }
    } catch (error) {
      console.warn('[add-to-cart-upsell] Set panel chain skipped', error);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Set panel                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Populates and opens the "Complete the set" panel.
   *
   * @param {string | undefined} frameValue - The frame style just chosen, used
   *   to match the sibling products' variants. Undefined when the triggering
   *   variant has no frame option (or the frame option index isn't known) —
   *   siblings then fall back to their first available variant.
   * @param {any} [knownCart] - Cart object, if already fetched.
   */
  async #prepareSetPanel(frameValue, knownCart) {
    if (this.#config.context === 'product' && !this.#config.enableSet) return;
    if (this.#isDismissed('set')) return;

    this.#panelPlacement = 'post_add_to_cart_modal';

    const container = this.refs.setProducts;
    if (!container) return;

    let cart = knownCart;
    if (!cart) {
      try {
        cart = await this.#fetchCart();
      } catch (error) {
        console.error('[add-to-cart-upsell] Unable to fetch cart', error);
        return;
      }
    }

    const productIdsInCart = new Set(
      cart.items.map(/** @param {any} item */ (item) => String(item.product_id))
    );

    let anyVisible = false;

    for (const tile of container.querySelectorAll('[data-sibling-id]')) {
      if (!(tile instanceof HTMLElement)) continue;

      const siblingId = tile.dataset.siblingId ?? '';
      const product = this.#config.set.products.find(
        /** @param {any} p */ (p) => String(p.id) === siblingId
      );

      // Skip products that are unknown or already in the basket.
      if (!product || productIdsInCart.has(siblingId)) {
        tile.hidden = true;
        continue;
      }

      // Prefer the variant matching the frame style just chosen, when known.
      let variant = null;
      if (product.frameOptionIndex >= 0 && frameValue) {
        variant = this.#findVariantByFrameValue(product, frameValue);
      }
      if (!variant) {
        variant = product.variants.find(/** @param {any} v */ (v) => v.available) ?? null;
      }

      if (!variant) {
        tile.hidden = true;
        continue;
      }

      tile.hidden = false;
      tile.dataset.selected = 'true';
      tile.setAttribute('aria-pressed', 'true');
      tile.dataset.variantId = String(variant.id);
      tile.dataset.priceCents = String(variant.priceCents);

      const priceElement = tile.querySelector('[data-price]');
      if (priceElement) priceElement.textContent = variant.price;

      anyVisible = true;
    }

    if (!anyVisible) return;

    this.#updateSetTotals();
    this.#showError(this.refs.setError, null);
    this.#open('set');
  }

  /**
   * Toggles a set product tile in or out of the selection.
   *
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  toggleSetItem(event) {
    const tile = event.target.closest('[data-sibling-id]');
    if (!(tile instanceof HTMLElement) || tile.hidden) return;

    const selected = tile.dataset.selected !== 'true';
    tile.dataset.selected = selected ? 'true' : 'false';
    tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
    this.#updateSetTotals();
  }

  /**
   * @returns {HTMLElement[]} The currently selected, visible set tiles.
   */
  #selectedSetTiles() {
    const container = this.refs.setProducts;
    if (!container) return [];
    return Array.from(container.querySelectorAll('[data-sibling-id]')).filter(
      (tile) =>
        tile instanceof HTMLElement && !tile.hidden && tile.dataset.selected === 'true'
    );
  }

  #updateSetTotals() {
    const tiles = this.#selectedSetTiles();
    const count = tiles.length;
    const totalCents = tiles.reduce(
      (sum, tile) => sum + (parseInt(tile.dataset.priceCents ?? '0', 10) || 0),
      0
    );

    const { setCount, setTotal, setCta, setCtaLabel } = this.refs;

    if (setCount) {
      setCount.textContent = count === 1 ? '1 print selected' : `${count} prints selected`;
    }
    if (setTotal) {
      setTotal.textContent = count > 0 ? this.#formatMoney(totalCents) ?? '' : '';
    }
    if (setCta) setCta.disabled = count === 0;
    if (setCtaLabel) {
      setCtaLabel.textContent =
        count === 0
          ? 'Select a print'
          : count === 1
            ? 'Add to basket'
            : count === 2
              ? 'Add both to basket'
              : `Add ${count} prints to basket`;
    }
  }

  /**
   * Adds the selected set products to the cart.
   */
  async addSet() {
    if (this.#busy) return;

    const tiles = this.#selectedSetTiles();
    if (tiles.length === 0) return;

    this.#setBusy(true);
    this.#showError(this.refs.setError, null);

    try {
      const items = tiles.map((tile) => ({
        id: Number(tile.dataset.variantId),
        quantity: 1,
      }));

      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const addResult = await addResponse.json();
      if (addResult.status) {
        throw new Error(addResult.description || addResult.message || 'Unable to add to cart');
      }

      const cart = await this.#fetchCart();
      this.#baseline = this.#quantitiesFor(cart);

      this.#setBusy(false);
      this.#markDismissed('set');

      const totalCents = tiles.reduce(
        (sum, tile) => sum + (parseInt(tile.dataset.priceCents ?? '0', 10) || 0),
        0
      );

      pushDataLayerEvent('upsell_accepted', {
        ...UPSELL_META.set,
        placement: this.#panelPlacement,
        item_id: this.#config?.productId,
        product_name: this.#productName(),
        // £ value of the accepted upsell — the sibling print(s) that were
        // actually added, same price × quantity convention as
        // add_to_cart_detail's `value` (quantity is always 1 per tile here).
        value: totalCents / 100,
        currency: currentCurrency(),
      });

      this.#notifyCartUpdated(cart);
      this.close();
    } catch (error) {
      console.error('[add-to-cart-upsell] Complete-the-set failed', error);
      this.#showError(
        this.refs.setError,
        'Something went wrong updating your basket. Please try again.'
      );
      this.#setBusy(false);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Shared helpers                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * @param {string | undefined} value
   * @param {string | undefined} noFrameValue
   * @returns {boolean}
   */
  #isNoFrame(value, noFrameValue) {
    const noFrame = (noFrameValue || '').toLowerCase();
    if (!noFrame || !value) return false;
    return value.toLowerCase() === noFrame;
  }

  /**
   * Finds the variant of `config` that matches `referenceOptions` in every
   * position except the frame option, which must equal `frameValue`.
   *
   * @param {any} config - Object with `variants` and `frameOptionIndex`.
   * @param {string[]} referenceOptions - Option values of the reference variant.
   * @param {string} frameValue - Desired frame style value.
   * @returns {any | null}
   */
  #findMatchingVariant(config, referenceOptions, frameValue) {
    return (
      config.variants.find(
        /** @param {any} v */ (v) =>
          v.available &&
          v.options.every(
            /**
             * @param {string} option
             * @param {number} index
             */
            (option, index) =>
              index === config.frameOptionIndex
                ? option.toLowerCase() === frameValue.toLowerCase()
                : option === referenceOptions[index]
          )
      ) ?? null
    );
  }

  /**
   * Finds an available variant of a sibling product with the given frame value.
   *
   * @param {any} product
   * @param {string} frameValue
   * @returns {any | null}
   */
  #findVariantByFrameValue(product, frameValue) {
    return (
      product.variants.find(
        /** @param {any} v */ (v) =>
          v.available &&
          (v.options[product.frameOptionIndex] ?? '').toLowerCase() === frameValue.toLowerCase()
      ) ?? null
    );
  }

  /**
   * @returns {Promise<any>} The cart object from the AJAX API.
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  /**
   * Maps this product's variant ids to their quantities in the given cart.
   *
   * @param {any} cart
   * @returns {Map<string, number>}
   */
  #quantitiesFor(cart) {
    const quantities = new Map();

    // Cart context has no variants config — the baseline diff only applies
    // on product pages.
    const variants = this.#config.variants;
    if (!Array.isArray(variants)) return quantities;

    const ids = new Set(variants.map(/** @param {any} v */ (v) => String(v.id)));
    for (const item of cart.items) {
      const id = String(item.variant_id);
      if (ids.has(id)) quantities.set(id, (quantities.get(id) ?? 0) + item.quantity);
    }
    return quantities;
  }

  /**
   * Dispatches a cart update so the cart drawer, cart items, cart bubble and
   * any other listeners (e.g. the frame upsell hero) refresh themselves.
   *
   * @param {any} cart - The latest cart object from the AJAX API.
   * @param {string} [variantId]
   */
  #notifyCartUpdated(cart, variantId) {
    const sourceId = this.id || 'add-to-cart-upsell';
    const data = {
      source: 'add-to-cart-upsell',
      itemCount: cart.item_count,
      variantId,
    };

    try {
      this.dispatchEvent(new CartUpdateEvent(cart, sourceId, data));
    } catch (error) {
      // Theme version mismatch on the event class — dispatch the same shape
      // as a plain CustomEvent so cart components still refresh.
      console.warn('[add-to-cart-upsell] CartUpdateEvent unavailable, using CustomEvent', error);
      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId, data },
        })
      );
    }
  }

  /**
   * Shows the requested panel. Opening is slightly delayed so the modal
   * stacks above the auto-opening cart drawer in the browser top layer.
   * The body scroll lock is intentionally left to the cart drawer.
   *
   * @param {'frame' | 'set'} panel
   */
  #open(panel) {
    for (const element of this.querySelectorAll('[data-panel]')) {
      if (element instanceof HTMLElement) {
        element.hidden = element.dataset.panel !== panel;
      }
    }

    this.#currentPanel = panel;

    const shownItem = this.#panelTrackingItem(panel);
    pushDataLayerEvent('upsell_shown', {
      ...UPSELL_META[panel],
      placement: this.#panelPlacement,
      item_id: shownItem.itemId,
      product_name: shownItem.productName,
      items: shownItem.items,
    });

    this.#showPending = true;
    setTimeout(() => {
      this.#showPending = false;
      if (!this.refs.dialog.open && this.isConnected) {
        this.refs.dialog.showModal();
      }
    }, OPEN_DELAY);
  }

  /**
   * Dismisses the modal and remembers the choice for this product and panel
   * for the rest of the session, so repeat adds don't nag the customer.
   */
  dismiss = () => {
    // Not a decline while a confirm is already in flight (e.g. Esc pressed
    // mid-upgrade) — that confirm reports its own outcome.
    if (this.#currentPanel && !this.#busy) {
      pushDataLayerEvent('upsell_declined', this.#declinedParams(this.#currentPanel));
    }
    if (this.#checkoutIntent) {
      // Closing via X / backdrop / Esc after a checkout intercept: just close
      // and re-arm — the customer may want to edit their basket first.
      this.#checkoutIntent = false;
      this.close();
      return;
    }
    if (this.#currentPanel) this.#markDismissed(this.#currentPanel);
    this.close();
  };

  /**
   * @param {'frame' | 'set'} panel
   * @returns {Record<string, unknown>}
   */
  #declinedParams(panel) {
    const declinedItem = this.#panelTrackingItem(panel);
    return {
      ...UPSELL_META[panel],
      placement: this.#panelPlacement,
      item_id: declinedItem.itemId,
      product_name: declinedItem.productName,
      items: declinedItem.items,
    };
  }

  /**
   * Sends events that must survive an immediate navigation (the checkout
   * hand-off): waits for gtag's event_callback on each, capped at
   * ANALYTICS_FLUSH_TIMEOUT so a blocked or slow gtag.js never holds up
   * checkout.
   *
   * @param {[string, Record<string, unknown>][]} events
   * @returns {Promise<void>}
   */
  #sendBeforeNavigation(events) {
    if (typeof window.gtag !== 'function' || events.length === 0) return Promise.resolve();

    return new Promise((resolve) => {
      let pending = events.length;
      const timer = setTimeout(resolve, ANALYTICS_FLUSH_TIMEOUT);
      const done = () => {
        pending -= 1;
        if (pending > 0) return;
        clearTimeout(timer);
        resolve();
      };
      for (const [name, params] of events) {
        pushDataLayerEvent(name, { ...params, event_callback: done, event_timeout: ANALYTICS_FLUSH_TIMEOUT });
      }
    });
  }

  /**
   * Best-effort product name for upsell events, read from the rendered
   * panel — or from a specific row when one is known (e.g. a single
   * confirmed frame selection).
   * @param {HTMLElement} [row]
   */
  #productName(row) {
    return (row ?? this).querySelector('.atc-upsell__item-title')?.textContent?.trim();
  }

  /**
   * Resolves item_id/product_name for panel-level events (upsell_shown /
   * upsell_declined). The cart-context frame panel can list one row per
   * unframed line with its own real product id: item_id/product_name report
   * the first visible row's identity (instead of the panel-level "cart"
   * placeholder), and `items` lists every row offered. Product context and
   * the "set" panel always cover exactly one real product.
   * @param {'frame' | 'set'} panel
   * @returns {{ itemId: string | undefined, productName: string | undefined, items: { item_id: string | undefined, item_name: string | undefined }[] }}
   */
  #panelTrackingItem(panel) {
    if (panel === 'frame' && this.#config?.context === 'cart') {
      const rows = Array.from(this.refs.frameItems?.querySelectorAll('[data-frame-item]:not([hidden])') ?? []).filter(
        (row) => row instanceof HTMLElement
      );
      if (rows.length > 0) {
        const items = rows.map((row) => ({
          item_id: /** @type {HTMLElement} */ (row).dataset.productId || this.#config?.productId,
          item_name: this.#productName(/** @type {HTMLElement} */ (row)),
        }));
        return { itemId: items[0].item_id, productName: items[0].item_name, items };
      }
    }
    const itemId = this.#config?.productId;
    const productName = this.#productName();
    return { itemId, productName, items: [{ item_id: itemId, item_name: productName }] };
  }

  /**
   * Closes the dialog with the theme's closing animation.
   */
  close = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    try {
      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, { subtree: false });
    } catch (_) {
      // Animation helper differences between theme versions — close anyway.
    } finally {
      dialog.classList.remove('dialog-closing');
      dialog.close();
    }
  };

  /**
   * @param {'frame' | 'set'} panel
   * @returns {boolean}
   */
  #isDismissed(panel) {
    try {
      return sessionStorage.getItem(this.#dismissKey(panel)) === 'true';
    } catch (_) {
      return false;
    }
  }

  /**
   * @param {'frame' | 'set'} panel
   */
  #markDismissed(panel) {
    try {
      sessionStorage.setItem(this.#dismissKey(panel), 'true');
    } catch (_) {
      // Storage unavailable — modal will simply show again.
    }
  }

  /**
   * @param {'frame' | 'set'} panel
   * @returns {string}
   */
  #dismissKey(panel) {
    return `add-to-cart-upsell:${panel}:${this.#config.productId}`;
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.dialog.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {string | null} message
   */
  #showError(element, message) {
    if (!element) return;
    element.hidden = !message;
    element.textContent = message ?? '';
  }

  /**
   * Formats a minor-unit amount using the shop currency. Returns null when
   * formatting isn't possible so callers can fall back to server-rendered
   * prices.
   *
   * @param {number} cents
   * @returns {string | null}
   */
  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.#config.locale || undefined, {
        style: 'currency',
        currency: this.#config.currency,
      }).format(cents / 100);
    } catch (_) {
      return null;
    }
  }
}

if (!customElements.get('add-to-cart-upsell')) {
  customElements.define('add-to-cart-upsell', AddToCartUpsell);
}